import { useState, useEffect } from 'react';
import { aquaApi } from '../aquaApi';

const EMPTY = {
  cropCode: '', equipmentName: '', brand: '', spec: '',
  uom: 'Nos', qtySvs: '0', qtyPrg: '0', qtyTotal: '0', cost: '0', notes: ''
};

export default function AquaEquipment({ activeCrops = [], role = 'admin' }) {
  const canWrite = role !== 'readonly';
  const canDelete = role === 'admin';
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [tab, setTab] = useState('list');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = () => aquaApi.getEquipment().then(setRows).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(f => {
    const next = { ...f, [k]: v };
    if (k === 'qtySvs' || k === 'qtyPrg') {
      next.qtyTotal = ((parseFloat(k === 'qtySvs' ? v : next.qtySvs) || 0) + (parseFloat(k === 'qtyPrg' ? v : next.qtyPrg) || 0)).toString();
    }
    return next;
  });

  const handleSave = async () => {
    if (!form.equipmentName) { setErr('Equipment name is required'); return; }
    try {
      if (editId) await aquaApi.updateEquipment(editId, form);
      else await aquaApi.createEquipment(form);
      setForm(EMPTY); setEditId(null); setErr(''); setTab('list'); load();
    } catch (e) { setErr(e.message); }
  };

  const handleEdit = (r) => {
    setForm({
      cropCode: r.crop_code || '', equipmentName: r.equipment_name || '',
      brand: r.brand || '', spec: r.spec || '', uom: r.uom || 'Nos',
      qtySvs: r.qty_svs || '0', qtyPrg: r.qty_prg || '0',
      qtyTotal: r.qty_total || '0', cost: r.cost || '0', notes: r.notes || ''
    });
    setEditId(r.id); setTab('form');
  };

  const filtered = rows.filter(r =>
    !search || (r.equipment_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (r.brand || '').toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="loading-msg">Loading equipment…</div>;

  return (
    <div>
      <div className="tabs">
        <button className={`tab${tab === 'list' ? ' active' : ''}`} onClick={() => { setTab('list'); setEditId(null); setForm(EMPTY); }}>📋 Equipment List</button>
        {canWrite && <button className={`tab${tab === 'form' ? ' active' : ''}`} onClick={() => setTab('form')}>{editId ? '✏️ Edit' : '➕ Add Equipment'}</button>}
      </div>

      {tab === 'list' && (
        <div>
          <div className="stats-grid" style={{ marginBottom: 16 }}>
            <div className="stat-card">
              <div className="stat-icon blue" style={{ fontSize: 22 }}>🔧</div>
              <div><div className="stat-label">Total Items</div><div className="stat-value">{rows.length}</div></div>
            </div>
            <div className="stat-card">
              <div className="stat-icon green" style={{ fontSize: 22 }}>📦</div>
              <div><div className="stat-label">Total Qty</div><div className="stat-value">{rows.reduce((s, r) => s + parseFloat(r.qty_total || 0), 0)}</div></div>
            </div>
            <div className="stat-card">
              <div className="stat-icon orange" style={{ fontSize: 22 }}>💰</div>
              <div><div className="stat-label">Total Cost</div><div className="stat-value">₹{Number(rows.reduce((s, r) => s + parseFloat(r.cost || 0), 0)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div></div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">🔧 Equipment Inventory</div>
              <input className="form-control" style={{ width: 220, padding: '4px 10px' }} placeholder="Search equipment…" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Equipment Name</th><th>Brand</th><th>Spec</th><th>UOM</th>
                    <th style={{ textAlign: 'right' }}>SVS Qty</th>
                    <th style={{ textAlign: 'right' }}>PRG Qty</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                    <th style={{ textAlign: 'right' }}>Cost (₹)</th>
                    <th>Crop</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && <tr><td colSpan={10} style={{ textAlign: 'center', color: '#888', padding: 24 }}>{search ? 'No matches' : 'No equipment recorded'}</td></tr>}
                  {filtered.map(r => (
                    <tr key={r.id}>
                      <td style={{ fontWeight: 500 }}>{r.equipment_name}</td>
                      <td>{r.brand}</td>
                      <td>{r.spec}</td>
                      <td>{r.uom}</td>
                      <td style={{ textAlign: 'right' }}>{r.qty_svs}</td>
                      <td style={{ textAlign: 'right' }}>{r.qty_prg}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{r.qty_total}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: '#e37400' }}>
                        {parseFloat(r.cost || 0) > 0 ? `₹${Number(r.cost).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—'}
                      </td>
                      <td>{r.crop_code && <span style={{ background: '#f1f3f4', borderRadius: 8, padding: '2px 8px', fontSize: 12 }}>{r.crop_code}</span>}</td>
                      <td>
                        {canWrite && <button className="btn-sm btn-secondary" onClick={() => handleEdit(r)} style={{ marginRight: 4 }}>Edit</button>}
                        {canDelete && <button className="btn-sm btn-danger" onClick={async () => { if (window.confirm('Delete?')) { await aquaApi.deleteEquipment(r.id); load(); } }}>Del</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === 'form' && (
        <div className="card">
          <div className="card-header"><div className="card-title">{editId ? '✏️ Edit Equipment' : '➕ Add Equipment'}</div></div>
          <div className="card-body">
            {err && <div className="alert alert-error" style={{ marginBottom: 12 }}>{err}</div>}
            <div className="form-grid cols-3">
              <div className="form-group" style={{ gridColumn: 'span 2' }}>
                <label className="form-label">Equipment Name *</label>
                <input className="form-control" placeholder="DOUBLE PADDLE AERATOR, STARTER, etc." value={form.equipmentName} onChange={e => set('equipmentName', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Brand</label>
                <input className="form-control" placeholder="MEX, LUBI, etc." value={form.brand} onChange={e => set('brand', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Spec (HP, size, etc.)</label>
                <input className="form-control" placeholder="2 HP, 3 inch, etc." value={form.spec} onChange={e => set('spec', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">UOM</label>
                <input className="form-control" placeholder="Nos, SETs, etc." value={form.uom} onChange={e => set('uom', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Crop</label>
                <select className="form-control" value={form.cropCode} onChange={e => set('cropCode', e.target.value)}>
                  <option value="">— None / Common —</option>
                  {activeCrops.slice().sort((a, b) => a.crop_code.localeCompare(b.crop_code)).map(c => <option key={c.id} value={c.crop_code}>{c.crop_code}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Qty SVS (Farm 1)</label>
                <input className="form-control" type="number" step="0.5" value={form.qtySvs} onChange={e => set('qtySvs', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Qty PRG (Farm 2)</label>
                <input className="form-control" type="number" step="0.5" value={form.qtyPrg} onChange={e => set('qtyPrg', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Total Qty</label>
                <input className="form-control" type="number" step="0.5" value={form.qtyTotal} onChange={e => set('qtyTotal', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Total Cost (₹)</label>
                <input className="form-control" type="number" step="0.01" min="0" placeholder="0.00"
                  value={form.cost} onChange={e => set('cost', e.target.value)} />
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Notes</label>
                <input className="form-control" value={form.notes} onChange={e => set('notes', e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button className="btn btn-primary" onClick={handleSave}>{editId ? 'Update' : 'Save'}</button>
              <button className="btn btn-secondary" onClick={() => { setTab('list'); setEditId(null); setForm(EMPTY); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
